import { useCallback, useEffect, useState } from 'react';
import type { Fact, Mood } from '@elena/shared';
import { Icon, type IconName } from '../components/Icon';
import { api, ApiError, type CommentatorSettings, type DictionaryEntry } from '../lib/api';
import { engineLabels, useVoiceStatus, warmVoice } from '../lib/listen';
import { languageInfo, languages, usd, type Origin } from '@elena/shared';
import { session } from '../lib/session';
import { speak } from '../lib/speech';
import { wakeLabel, type Wake } from '../lib/useWake';
import { canWake } from '../lib/wake';
import { useElena } from '../state';

const originOptions: Array<{ value: Origin; label: string; hint: string }> = [
  { value: 'pc', label: 'Računar', hint: 'Najviše poverenja: ovo je tvoj PC kod kuće' },
  {
    value: 'phone',
    label: 'Telefon',
    hint: 'Strože: opasne akcije traže potvrdu ili su zabranjene',
  },
  { value: 'web', label: 'Web', hint: 'Najstrože: za browser van kuće' },
];

export function Settings({ wake }: { wake: Wake }) {
  const { status, logout } = useElena();
  const [origin, setOrigin] = useState<Origin>(session.origin());
  const [speakAll, setSpeakAll] = useState(session.speakReplies());

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Podešavanja</h1>
          <p className="muted">Kako se ovaj uređaj predstavlja Eleni i kako ti odgovara.</p>
        </div>
      </header>

      <nav className="more-links" aria-label="Moduli">
        {moreLinks.map((l) => (
          <a key={l.href} href={l.href} className="more-link">
            <Icon name={l.icon} size={20} />
            {l.label}
          </a>
        ))}
      </nav>

      <div className="grid-2">
        <section className="card">
          <h2>Ovaj uređaj je</h2>
          <div className="radio-list" role="radiogroup" aria-label="Vrsta uređaja">
            {originOptions.map((o) => (
              <label key={o.value} className={`radio${origin === o.value ? ' is-checked' : ''}`}>
                <input
                  type="radio"
                  name="origin"
                  value={o.value}
                  checked={origin === o.value}
                  onChange={() => {
                    setOrigin(o.value);
                    session.setOrigin(o.value);
                  }}
                />
                <span className="grow">
                  <span className="strong">{o.label}</span>
                  <span className="muted small block">{o.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </section>

        <section className="card">
          <h2>Glas</h2>
          <label className="switch-row">
            <span className="grow">
              <span className="strong">Čitaj svaki odgovor naglas</span>
              <span className="muted small block">
                Kad je isključeno, Elena priča samo kad i ti pričaš glasom.
              </span>
            </span>
            <input
              type="checkbox"
              checked={speakAll}
              onChange={(e) => {
                setSpeakAll(e.target.checked);
                session.setSpeakReplies(e.target.checked);
              }}
            />
          </label>
          <p className="muted small">
            {status?.voice.enabled
              ? `Glas sa servera: ${status.voice.name}`
              : 'Server nema podešen Azure glas, koristi se glas browsera. Uputstvo je u docs/SETUP.md.'}
          </p>
          <button
            type="button"
            className="btn"
            onClick={() =>
              void speak(
                'Ćao, ovako zvučim. Tu sam kad god zatrebam.',
                Boolean(status?.voice.enabled),
              )
            }
          >
            Pusti probu glasa
          </button>
        </section>

        <section className="card">
          <h2>„Hej Elena“</h2>
          <label className="switch-row">
            <span className="grow">
              <span className="strong">Slušaj kad me pozoveš</span>
              <span className="muted small block">
                Dok je ova stranica otvorena, reci „Hej Elena, pusti Cecu“. Samo ime otvara mikrofon
                za sledeću rečenicu.
              </span>
            </span>
            <input
              type="checkbox"
              checked={wake.enabled}
              disabled={!canWake()}
              onChange={(e) => wake.setEnabled(e.target.checked)}
            />
          </label>
          <p className="muted small" aria-live="polite">
            {!canWake()
              ? 'Radi u Chrome-u i Edge-u.'
              : wake.state === 'error'
                ? wake.message
                : `Stanje: ${wakeLabel(wake)}. Chrome šalje zvuk Google-u na prepoznavanje, kao i dugme sa mikrofonom.`}
          </p>
        </section>

        <MemoryCard />
        <WeatherCityCard />
        <LanguageCard />
        <SpeechCard />
        <DictionaryCard />
        <AdminLineCard />
        <MoodCard />
        <CommentatorCard />

        <section className="card">
          <h2>Claude</h2>
          <dl className="facts">
            <div>
              <dt>Model</dt>
              <dd>{status?.model ?? '…'}</dd>
            </div>
            <div>
              <dt>Ovog meseca</dt>
              <dd>
                {status
                  ? `${usd(status.budget.usd)} od ${status.budget.limitUsd ? usd(status.budget.limitUsd) : 'neograničeno'}`
                  : '…'}
              </dd>
            </div>
            <div>
              <dt>Zahteva</dt>
              <dd>{status?.budget.requests ?? '…'}</dd>
            </div>
          </dl>
          <p className="muted small">Limit menjaš u .env na serveru (MONTHLY_BUDGET_USD).</p>
        </section>

        <section className="card">
          <h2>Nalog</h2>
          <p className="muted">Token se čuva samo u ovom browseru.</p>
          <button type="button" className="btn danger" onClick={logout}>
            Odjavi ovaj uređaj
          </button>
        </section>
      </div>
    </div>
  );
}

const moreLinks: Array<{ href: string; label: string; icon: IconName }> = [
  { href: '#/chat', label: 'Razgovor', icon: 'chat' },
  { href: '#/calendar', label: 'Kalendar', icon: 'calendar' },
  { href: '#/notes', label: 'Beleške', icon: 'note' },
  { href: '#/games', label: 'Igre', icon: 'game' },
  { href: '#/stats', label: 'Statistika', icon: 'chart' },
  { href: '#/integrations', label: 'Integracije', icon: 'plug' },
  { href: '#/permissions', label: 'Dozvole', icon: 'shield' },
];

function MemoryCard() {
  const [facts, setFacts] = useState<Fact[] | null>(null);
  const [text, setText] = useState('');

  const load = useCallback(() => {
    api
      .facts()
      .then((r) => setFacts(r.facts))
      .catch(() => setFacts([]));
  }, []);
  useEffect(load, [load]);

  return (
    <section className="card">
      <h2>Šta Elena zna o meni</h2>
      <p className="muted small">
        Pamti ono što joj kažeš o sebi („zapamti da se mama zove Vesna“) i koristi to u razgovoru.
        „Zaboravi …“ ili dugme briše.
      </p>
      {facts && facts.length === 0 && (
        <p className="muted">Još ništa. Ispričaj joj nešto o sebi.</p>
      )}
      <ul className="list">
        {(facts ?? []).map((f) => (
          <li key={f.id} className="list-row">
            <Icon name="heart" size={16} />
            <span className="grow">{f.text}</span>
            <button
              type="button"
              className="icon-btn ghost"
              aria-label={`Zaboravi: ${f.text}`}
              onClick={async () => {
                await api.removeFact(f.id).catch(() => undefined);
                load();
              }}
            >
              <Icon name="x" size={16} />
            </button>
          </li>
        ))}
      </ul>
      <form
        className="row gap-8"
        onSubmit={async (e) => {
          e.preventDefault();
          if (text.trim().length < 3) return;
          await api.addFact(text.trim()).catch(() => undefined);
          setText('');
          load();
        }}
      >
        <label className="sr-only" htmlFor="fact-text">
          Nova činjenica
        </label>
        <input
          id="fact-text"
          className="field"
          placeholder="Npr. Navijam za Zvezdu"
          value={text}
          maxLength={200}
          onChange={(e) => setText(e.target.value)}
        />
        <button type="submit" className="btn" disabled={text.trim().length < 3}>
          Dodaj
        </button>
      </form>
    </section>
  );
}

function WeatherCityCard() {
  const [city, setCity] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    api
      .weather()
      .then((r) => {
        setSaved(r.home);
        setCity(r.home);
      })
      .catch(() => undefined);
  }, []);

  return (
    <section className="card">
      <h2>Vreme</h2>
      <p className="muted small">
        Kućni grad za prognozu i jutarnji izveštaj. Za druge gradove samo pitaj: „kakvo je vreme u
        Nišu sutra?“
      </p>
      <form
        className="row gap-8"
        onSubmit={async (e) => {
          e.preventDefault();
          setNote(null);
          try {
            const r = await api.setHomeCity(city.trim());
            setSaved(r.home);
            setCity(r.home);
            setNote(`Sačuvano: ${r.home}.`);
          } catch (err) {
            setNote(err instanceof ApiError ? err.message : 'Ne znam taj grad.');
          }
        }}
      >
        <label className="sr-only" htmlFor="home-city">
          Kućni grad
        </label>
        <input
          id="home-city"
          className="field"
          value={city}
          maxLength={80}
          onChange={(e) => setCity(e.target.value)}
        />
        <button type="submit" className="btn" disabled={!city.trim() || city.trim() === saved}>
          Sačuvaj
        </button>
      </form>
      {note && (
        <p className="muted small" role="status">
          {note}
        </p>
      )}
    </section>
  );
}

const minutes = (seconds: number) => `${Math.round(seconds / 60)} min`;

/** The server's recognizer or the browser for the mic on this device, and this month's use. */
function SpeechCard() {
  const { status } = useElena();
  const [engine, setEngine] = useState(session.sttEngine());
  const voice = useVoiceStatus();
  const s = status?.speech;
  const choose = (next: 'server' | 'google') => {
    session.setSttEngine(next);
    setEngine(next);
    warmVoice();
  };
  const serverEngine = engine === 'server' && voice.engine !== 'browser' ? voice.engine : null;
  return (
    <section className="card">
      <h2>Prepoznavanje govora</h2>
      <p className="muted small">
        Za mikrofon, razgovor i „Hej Elena“ na ovom uređaju. Chrome uvek prepoznaje samo ime, a
        rečenicu posle njega čuje izabrani servis.
      </p>
      <div className="radio-list" role="radiogroup" aria-label="Prepoznavanje govora">
        {(
          [
            [
              'server',
              `Najbolje sa servera${serverEngine ? ` (${engineLabels[serverEngine]})` : ''}`,
              'Servis podešen na serveru (Soniox, ElevenLabs ili Azure): razume srpski i engleske nazive u istoj rečenici i zna imena igrača, soba i naučene reči.',
            ],
            ['google', 'Google (browser)', 'Ugrađeno u Chrome i Edge, besplatno, slabiji srpski.'],
          ] as const
        ).map(([id, label, hint]) => (
          <label key={id} className={`radio${engine === id ? ' is-checked' : ''}`}>
            <input
              type="radio"
              name="stt"
              value={id}
              checked={engine === id}
              onChange={() => choose(id)}
            />
            <span className="grow">
              <span className="strong">{label}</span>
              <span className="muted small block">{hint}</span>
            </span>
          </label>
        ))}
      </div>
      {voice.fallback && <p className="muted small">{voice.fallback}</p>}
      {s && (
        <dl className="facts">
          {s.paidStt?.soniox !== undefined && (
            <div>
              <dt>Soniox ovog meseca</dt>
              <dd>{minutes(s.paidStt.soniox)}</dd>
            </div>
          )}
          {s.paidStt?.elevenlabs !== undefined && (
            <div>
              <dt>ElevenLabs Scribe ovog meseca</dt>
              <dd>{minutes(s.paidStt.elevenlabs)}</dd>
            </div>
          )}
          <div>
            <dt>Azure prepoznavanje</dt>
            <dd>
              {minutes(s.stt.usedSeconds)} od {minutes(s.stt.limitSeconds)}
              {s.stt.available ? '' : ' · potrošeno'}
            </dd>
          </div>
          {s.eleven && s.eleven.limitChars > 0 && (
            <div>
              <dt>ElevenLabs glas</dt>
              <dd>
                {s.eleven.usedChars.toLocaleString('sr-RS')} od{' '}
                {s.eleven.limitChars.toLocaleString('sr-RS')} znakova
                {s.eleven.available ? '' : ' · potrošeno, priča Azure Sophie'}
              </dd>
            </div>
          )}
          <div>
            <dt>Azure glas</dt>
            <dd>
              {s.tts.usedChars.toLocaleString('sr-RS')} od{' '}
              {s.tts.limitChars.toLocaleString('sr-RS')} znakova
              {s.tts.available ? '' : ' · potrošeno, radi glas browsera'}
            </dd>
          </div>
        </dl>
      )}
    </section>
  );
}

/** ADMIN LINE: Elena calls players for you from the admin panel; her phone carries the voice. */
function AdminLineCard() {
  const { status } = useElena();
  const line = status?.adminLine;
  if (!line?.configured) return null;
  return (
    <section className="card">
      <h2>Elena na ADMIN LINIJI</h2>
      <p className="muted small">
        U admin panelu pritisni ELENA kod igrača: ona ga zove, priča sa njim, a ti gledaš razgovor i
        možeš da joj šapneš uputstvo.
      </p>
      <dl className="facts">
        <div>
          <dt>Admin panel</dt>
          <dd>{line.connected ? 'povezana' : 'nije povezana (proveri ACP_URL i nalog)'}</dd>
        </div>
        <div>
          <dt>Elenin telefon</dt>
          <dd>{line.phone ? 'radi' : 'ne radi (servis caller ili otvorena stranica)'}</dd>
        </div>
        {line.calls > 0 && (
          <div>
            <dt>Poziva u toku</dt>
            <dd>{line.calls}</dd>
          </div>
        )}
      </dl>
      {!line.phone && (
        <p className="small">
          <a href="#/caller" target="_blank" rel="noreferrer">
            Otvori Elenin telefon u ovom browseru
          </a>{' '}
          <span className="muted">(radi dok je kartica otvorena)</span>
        </p>
      )}
    </section>
  );
}

/** Words Elena learned from corrections ("david geta" = "David Guetta"); also addable here. */
function DictionaryCard() {
  const [entries, setEntries] = useState<DictionaryEntry[] | null>(null);
  const [heard, setHeard] = useState('');
  const [meant, setMeant] = useState('');
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    api
      .dictionary()
      .then((r) => setEntries(r.entries))
      .catch(() => setEntries([]));
  }, []);

  return (
    <section className="card">
      <h2>Reči koje je Elena naučila</h2>
      <p className="muted small">
        Kad je ispraviš („ne, mislio sam David Guetta“), zapamti kako je pogrešno čula i sledeći put
        razume. Možeš i sam da dodaš ime koje stalno pogreši.
      </p>
      {entries && entries.length === 0 && (
        <p className="muted">Još ništa. Ispravi je kad pogreši i ovde će se pojaviti.</p>
      )}
      <ul className="list">
        {(entries ?? []).map((e) => (
          <li key={e.id} className="list-row">
            <Icon name="wave" size={16} />
            <span className="grow">
              <span className="muted">„{e.heard}“</span> → <span className="strong">{e.meant}</span>
            </span>
            <button
              type="button"
              className="icon-btn ghost"
              aria-label={`Zaboravi: ${e.meant}`}
              onClick={async () => {
                const r = await api.forgetWord(e.id).catch(() => null);
                if (r) setEntries(r.entries);
              }}
            >
              <Icon name="x" size={16} />
            </button>
          </li>
        ))}
      </ul>
      <form
        className="row gap-8 wrap"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!heard.trim() || !meant.trim()) return;
          try {
            setEntries((await api.learnWord(heard.trim(), meant.trim())).entries);
            setHeard('');
            setMeant('');
            setNote(null);
          } catch (err) {
            setNote(err instanceof ApiError ? err.message : 'Nije sačuvano.');
          }
        }}
      >
        <label className="sr-only" htmlFor="word-heard">
          Kako je čuje
        </label>
        <input
          id="word-heard"
          className="field"
          placeholder="Čuje: david geta"
          value={heard}
          maxLength={80}
          onChange={(e) => setHeard(e.target.value)}
        />
        <label className="sr-only" htmlFor="word-meant">
          Šta znači
        </label>
        <input
          id="word-meant"
          className="field"
          placeholder="Znači: David Guetta"
          value={meant}
          maxLength={80}
          onChange={(e) => setMeant(e.target.value)}
        />
        <button type="submit" className="btn" disabled={!heard.trim() || !meant.trim()}>
          Nauči
        </button>
      </form>
      {note && (
        <p className="muted small" role="status">
          {note}
        </p>
      )}
    </section>
  );
}

function LanguageCard() {
  // the server is the source: it changes on its own when you talk in another language
  const { language } = useElena();
  const [note, setNote] = useState<string | null>(null);
  return (
    <section className="card">
      <h2>Jezik</h2>
      <p className="muted small">
        Elena odgovara na jeziku na kom joj se obratiš i sama prebaci mikrofon i glas. Glasom:
        „pričaj na engleskom“, „speak Serbian“, „sprich Deutsch“.
      </p>
      <div className="radio-list" role="radiogroup" aria-label="Jezik">
        {languages.map((l) => (
          <label key={l} className={`radio${language === l ? ' is-checked' : ''}`}>
            <input
              type="radio"
              name="language"
              value={l}
              checked={language === l}
              onChange={async () => {
                try {
                  setNote((await api.setLanguage(l)).text);
                } catch {
                  setNote('Nije sačuvano.');
                }
              }}
            />
            <span className="grow">
              <span className="strong">{languageInfo[l].label}</span>
            </span>
          </label>
        ))}
      </div>
      {note && (
        <p className="muted small" role="status">
          {note}
        </p>
      )}
    </section>
  );
}

function MoodCard() {
  const [mood, setMood] = useState<Mood | null>(null);
  const [list, setList] = useState<Array<{ id: Mood; label: string; hint: string }>>([]);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    api
      .mood()
      .then((r) => {
        setMood(r.mood);
        setList(r.moods);
      })
      .catch(() => undefined);
  }, []);

  return (
    <section className="card">
      <h2>Raspoloženje</h2>
      <p className="muted small">Isto možeš i glasom: „Elena, budi sarkastična“.</p>
      <div className="radio-list" role="radiogroup" aria-label="Raspoloženje">
        {list.map((m) => (
          <label key={m.id} className={`radio${mood === m.id ? ' is-checked' : ''}`}>
            <input
              type="radio"
              name="mood"
              value={m.id}
              checked={mood === m.id}
              onChange={async () => {
                setMood(m.id);
                try {
                  setNote((await api.setMood(m.id)).text);
                } catch {
                  setNote('Nije sačuvano.');
                }
              }}
            />
            <span className="grow">
              <span className="strong">{m.label}</span>
              <span className="muted small block">{m.hint}</span>
            </span>
          </label>
        ))}
      </div>
      {note && (
        <p className="muted small" role="status">
          {note}
        </p>
      )}
    </section>
  );
}

function CommentatorCard() {
  const [s, setS] = useState<CommentatorSettings | null>(null);
  const [friends, setFriends] = useState('');
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    api
      .commentator()
      .then((r) => {
        setS(r.settings);
        setFriends(r.settings.friends.join(', '));
      })
      .catch(() => undefined);
  }, []);

  if (!s) return null;
  const save = async (next: CommentatorSettings) => {
    setS(next);
    try {
      await api.saveCommentator(next);
      setNote('Sačuvano.');
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'Nije sačuvano.');
    }
  };
  const toggle = (key: keyof CommentatorSettings, label: string, hint: string) => (
    <label className="switch-row">
      <span className="grow">
        <span className="strong">{label}</span>
        <span className="muted small block">{hint}</span>
      </span>
      <input
        type="checkbox"
        checked={Boolean(s[key])}
        onChange={(e) => void save({ ...s, [key]: e.target.checked })}
      />
    </label>
  );

  return (
    <section className="card">
      <h2>Komentator</h2>
      <p className="muted small">
        Kad uđeš na server, Elena najavi: „Daaame i gospodo, {s.ownerName || 'šef'} je stigao!“
      </p>
      {toggle('enabled', 'Uključen', 'Prati ko ulazi i izlazi, padove i rekorde')}
      <label className="field-label">
        <span className="muted small">Tvoje ime u igri</span>
        <input
          className="field"
          value={s.ownerName}
          maxLength={40}
          onChange={(e) => setS({ ...s, ownerName: e.target.value })}
          onBlur={() => void save(s)}
        />
      </label>
      <label className="field-label">
        <span className="muted small">Drugari (zarezom)</span>
        <input
          className="field"
          value={friends}
          onChange={(e) => setFriends(e.target.value)}
          onBlur={() =>
            void save({
              ...s,
              friends: friends
                .split(',')
                .map((f) => f.trim())
                .filter(Boolean)
                .slice(0, 30),
            })
          }
        />
      </label>
      {toggle('everyone', 'Najavi svakoga', 'Ne samo tebe i drugare')}
      {toggle('records', 'Rekordi', 'Kad server ima najviše igrača u poslednje dve nedelje')}
      {toggle('speak', 'Izgovori na uređajima', 'Elena kaže naglas gde je otvorena')}
      {toggle('push', 'Notifikacija na telefon', 'I kad je aplikacija zatvorena')}
      {toggle('inGame', 'U igri (elena_bridge)', 'Najave drugara i rekorda u chat servera')}
      <label className="field-label">
        <span className="muted small">Discord kanal (ime iz Integracija, prazno = ne)</span>
        <input
          className="field"
          value={s.discord}
          maxLength={40}
          onChange={(e) => setS({ ...s, discord: e.target.value })}
          onBlur={() => void save(s)}
        />
      </label>
      <div className="row gap-8 wrap">
        <button
          type="button"
          className="btn"
          onClick={async () => {
            try {
              setNote(
                (await api.testCommentator()).text ?? 'Već najavljeno pre manje od 10 minuta.',
              );
            } catch (err) {
              setNote(err instanceof ApiError ? err.message : 'Nije uspelo.');
            }
          }}
        >
          Probaj najavu
        </button>
        {note && (
          <span className="muted small" role="status">
            {note}
          </span>
        )}
      </div>
    </section>
  );
}
